import { useMemo, useRef, useState, type ReactNode } from "react";
import { AtSign, Loader2, MessageSquare, Paperclip, Reply, X } from "lucide-react";

import { groupComments, type ThreadedComment } from "@/components/comments/groupComments";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type CommentAttachment = {
  id: number;
  filename: string;
  byte_size: number;
  content_type: string;
  url: string;
};

export type DisplayComment = ThreadedComment & {
  author_name: string;
  body: string;
  attachments?: CommentAttachment[];
};

export type MentionCandidate = { id: number; email: string };

const MENTION_TOKEN_PATTERN = /(@[^\s@]+@[^\s@]+)/g;
// An @ preceded by start-of-text/whitespace with a partial word after it is an
// active mention query; a completed @email token contains a second @ so it
// cannot re-trigger the typeahead.
const ACTIVE_MENTION_QUERY = /(?:^|\s)@([^\s@]*)$/;

function renderBodyWithMentions(body: string): ReactNode {
  const parts = body.split(MENTION_TOKEN_PATTERN);
  if (parts.length === 1) return body;

  return parts.map((part, index) =>
    /^@[^\s@]+@[^\s@]+$/.test(part) ? (
      <span key={`${part}-${index}`} className="font-medium text-violet-600">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

function AttachmentChips({ attachments }: { attachments?: CommentAttachment[] }) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.url}
          className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs text-foreground/80 hover:bg-muted"
        >
          <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="max-w-[180px] truncate">{attachment.filename}</span>
          <span className="text-muted-foreground">{formatFileSize(attachment.byte_size)}</span>
        </a>
      ))}
    </div>
  );
}

type CommentComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  placeholder?: string;
  submitLabel?: string;
  onCancel?: () => void;
  compact?: boolean;
  variant?: "default" | "sidebar";
  allowAttachments?: boolean;
  files?: File[];
  onFilesChange?: (files: File[]) => void;
  mentionCandidates?: MentionCandidate[];
  onMentionSelected?: (candidate: MentionCandidate) => void;
};

function CommentComposer({
  draft,
  onDraftChange,
  onSubmit,
  submitting,
  placeholder = "Leave a note for your team…",
  submitLabel = "Post comment",
  onCancel,
  compact = false,
  variant = "default",
  allowAttachments = false,
  files = [],
  onFilesChange,
  mentionCandidates,
  onMentionSelected,
}: CommentComposerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mentionQuery = useMemo(() => {
    if (!mentionCandidates?.length) return null;
    return ACTIVE_MENTION_QUERY.exec(draft)?.[1] ?? null;
  }, [draft, mentionCandidates]);

  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    const query = mentionQuery.toLowerCase();
    return (mentionCandidates ?? [])
      .filter((candidate) => candidate.email.toLowerCase().includes(query))
      .slice(0, 5);
  }, [mentionQuery, mentionCandidates]);

  const insertMention = (candidate: MentionCandidate) => {
    onDraftChange(draft.replace(/@[^\s@]*$/, `@${candidate.email} `));
    onMentionSelected?.(candidate);
  };

  const composer = (
    <>
      <Textarea
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        rows={compact ? 2 : 3}
        placeholder={placeholder}
        aria-label="New comment"
      />
      {mentionMatches.length > 0 ? (
        <div
          className="overflow-hidden rounded-md border bg-background shadow-sm"
          data-testid="mention-typeahead"
        >
          {mentionMatches.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-violet-50"
              onClick={() => insertMention(candidate)}
            >
              <AtSign className="h-3 w-3 text-violet-500" />
              {candidate.email}
            </button>
          ))}
        </div>
      ) : null}
      {allowAttachments && files.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 text-muted-foreground" />
              <span className="max-w-[180px] truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onFilesChange?.(files.filter((_, i) => i !== index))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex items-center justify-end gap-2">
        {allowAttachments ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
              aria-label="Attach files"
              onChange={(event) => {
                const selected = Array.from(event.target.files ?? []);
                if (selected.length > 0) onFilesChange?.([...files, ...selected]);
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="mr-auto text-muted-foreground"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="mr-1.5 h-3.5 w-3.5" />
              Attach
            </Button>
          </>
        ) : null}
        {onCancel ? (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button size="sm" disabled={!draft.trim() || submitting} onClick={onSubmit}>
          {submitting ? "Posting…" : submitLabel}
        </Button>
      </div>
    </>
  );

  if (variant === "sidebar") {
    return <div className="space-y-2">{composer}</div>;
  }

  return (
    <div className={cn("space-y-2 rounded-lg border bg-background", compact ? "p-2" : "p-3")}>
      {composer}
    </div>
  );
}

function CommentBody({
  comment,
  replies,
  onReply,
  renderCommentActions,
}: {
  comment: DisplayComment;
  replies: DisplayComment[];
  onReply: (commentId: number) => void;
  renderCommentActions?: (comment: DisplayComment) => ReactNode;
}) {
  return (
    <li className="rounded-lg border bg-muted/20 p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground/80">{comment.author_name}</span>
        <time dateTime={comment.created_at}>{formatRelativeTime(comment.created_at)}</time>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed">{renderBodyWithMentions(comment.body)}</p>
      <AttachmentChips attachments={comment.attachments} />
      <div className="mt-2 flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => onReply(comment.id)}
        >
          <Reply className="mr-1.5 h-3 w-3" />
          Reply
        </Button>
        {renderCommentActions?.(comment)}
      </div>
      {replies.length > 0 ? (
        <ul className="mt-3 space-y-2 border-l-2 border-border/60 pl-3">
          {replies.map((reply) => (
            <li key={reply.id} className="rounded-md bg-background/80 p-2.5">
              <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{reply.author_name}</span>
                <time dateTime={reply.created_at}>{formatRelativeTime(reply.created_at)}</time>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{renderBodyWithMentions(reply.body)}</p>
              <AttachmentChips attachments={reply.attachments} />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

type CommentThreadProps = {
  comments: DisplayComment[];
  isLoading?: boolean;
  error?: boolean;
  submitting?: boolean;
  onCreateComment: (
    body: string,
    parentId?: number | null,
    files?: File[],
    mentionedUserIds?: number[],
  ) => void | Promise<void>;
  testId?: string;
  emptyMessage?: string;
  className?: string;
  variant?: "default" | "sidebar";
  allowAttachments?: boolean;
  renderCommentActions?: (comment: DisplayComment) => ReactNode;
  /** Rendered above the top-level composer only (replies never see it). */
  composerExtras?: ReactNode;
  /** Enables the @ typeahead on the top-level composer. */
  mentionCandidates?: MentionCandidate[];
};

export function CommentThread({
  comments,
  isLoading = false,
  error = false,
  submitting = false,
  onCreateComment,
  testId = "comment-thread",
  emptyMessage = "No comments yet. Leave the first note for your team.",
  className,
  variant = "default",
  allowAttachments = false,
  renderCommentActions,
  composerExtras,
  mentionCandidates,
}: CommentThreadProps) {
  const [draft, setDraft] = useState("");
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  const [draftMentions, setDraftMentions] = useState<MentionCandidate[]>([]);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);

  const { topLevel, repliesByParent } = useMemo(() => groupComments(comments), [comments]);

  const handleCreate = async (body: string, parentId?: number | null, files?: File[]) => {
    // Prune ids whose @email token was edited out of the body, so a deleted
    // mention never becomes a phantom notification.
    const survivingIds = draftMentions
      .filter((mention) => body.includes(`@${mention.email}`))
      .map((mention) => mention.id);
    await onCreateComment(body, parentId, files, parentId ? undefined : survivingIds);
    setDraft("");
    setDraftFiles([]);
    setDraftMentions([]);
    setReplyDraft("");
    setReplyFiles([]);
    setReplyingTo(null);
  };

  return (
    <div className={cn("space-y-3", className)} data-testid={testId}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading comments…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load comments.</p>
      ) : topLevel.length === 0 ? (
        variant === "sidebar" ? (
          <p className="py-2 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-6 text-center">
            <MessageSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          </div>
        )
      ) : (
        <ul className="space-y-2">
          {topLevel.map((comment) => (
            <div key={comment.id}>
              <CommentBody
                comment={comment}
                replies={repliesByParent.get(comment.id) ?? []}
                onReply={setReplyingTo}
                renderCommentActions={renderCommentActions}
              />
              {replyingTo === comment.id ? (
                <div className="mt-2 ml-3">
                  <CommentComposer
                    draft={replyDraft}
                    onDraftChange={setReplyDraft}
                    submitting={submitting}
                    placeholder="Write a reply…"
                    submitLabel="Post reply"
                    compact
                    variant={variant}
                    allowAttachments={allowAttachments}
                    files={replyFiles}
                    onFilesChange={setReplyFiles}
                    onCancel={() => {
                      setReplyingTo(null);
                      setReplyDraft("");
                      setReplyFiles([]);
                    }}
                    onSubmit={() => void handleCreate(replyDraft.trim(), comment.id, replyFiles)}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </ul>
      )}

      {composerExtras}

      <CommentComposer
        draft={draft}
        onDraftChange={setDraft}
        submitting={submitting}
        variant={variant}
        allowAttachments={allowAttachments}
        files={draftFiles}
        onFilesChange={setDraftFiles}
        mentionCandidates={mentionCandidates}
        onMentionSelected={(candidate) =>
          setDraftMentions((current) =>
            current.some((mention) => mention.id === candidate.id) ? current : [...current, candidate],
          )
        }
        onSubmit={() => void handleCreate(draft.trim(), null, draftFiles)}
      />
    </div>
  );
}
